import { useMemo, useState } from "react";
import Chip from "@mui/material/Chip";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import AssessmentIcon from "@mui/icons-material/Assessment";
import type { GridColDef } from "@mui/x-data-grid";
import { api } from "../lib/api";
import { describeCheckError } from "../lib/check-error";
import { useAsync } from "../lib/hooks";
import { externalUrl, formatMs } from "../lib/format";
import { useI18n } from "../lib/i18n";
import type { MatrixCell, MatrixLink, Verdict } from "../lib/types";
import { Button, Card, Empty, ErrorBox, Loading } from "./ui";
import { DataTable } from "./DataTable";

const VERDICTS: Verdict[] = ["healthy", "partial", "down", "unknown"];

const VERDICT_COLOR: Record<Verdict, "success" | "warning" | "error" | "default"> = {
  healthy: "success",
  partial: "warning",
  down: "error",
  unknown: "default",
};

/** Một dòng của ma trận: thông tin link + mỗi cột nguồn là một ô động theo `column.key`. */
type MatrixRow = MatrixLink & Record<string, unknown>;

export function LinkMatrixView({
  onSelectLink,
}: {
  onSelectLink?: (link: MatrixLink) => void;
} = {}) {
  const { t } = useI18n();
  const [staleMinutes, setStaleMinutes] = useState(60);
  const [onlyProblems, setOnlyProblems] = useState(false);
  const { data, loading, error, reload } = useAsync(
    () => api.deviceChecks.matrix(staleMinutes),
    [staleMinutes],
  );

  const columnDefs = data?.columns ?? [];
  const links = data?.links ?? [];

  // "Có vấn đề" = partial (chặn cục bộ) + down (chết thật).
  const visible = onlyProblems
    ? links.filter(l => l.consensus.verdict === "partial" || l.consensus.verdict === "down")
    : links;

  const rows = useMemo<MatrixRow[]>(
    () =>
      visible.map(link => {
        // Tra ô theo key để cột luôn khớp header kể cả khi backend trả thiếu/khác thứ tự.
        const byKey = new Map(link.cells.map(c => [c.key, c]));
        const cells = Object.fromEntries(columnDefs.map(c => [c.key, byKey.get(c.key)]));
        return { ...link, ...cells };
      }),
    [visible, columnDefs],
  );

  const columns = useMemo<GridColDef<MatrixRow>[]>(() => {
    const base: GridColDef<MatrixRow>[] = [
      {
        field: "brand",
        headerName: t("table.brand"),
        width: 110,
        minWidth: 110,
        maxWidth: 110,
      },
      {
        field: "domain",
        headerName: t("table.domain"),
        width: 260,
        minWidth: 260,
        maxWidth: 260,
        renderCell: ({ row }) => {
          const { failingRegions, failingNetworks } = row.consensus;
          const detail = [
            failingRegions.length ? `${t("matrix.failingRegions")}: ${failingRegions.join(", ")}` : null,
            failingNetworks.length
              ? `${t("matrix.failingNetworks")}: ${failingNetworks.join(", ")}`
              : null,
          ]
            .filter(Boolean)
            .join(" · ");
          return (
            <Stack sx={{ py: 0.5, minWidth: 0, lineHeight: 1.35 }}>
              <Stack direction="row" spacing={0.75} sx={{ alignItems: "center" }}>
                <Link
                  href={externalUrl(row.domain)}
                  target="_blank"
                  rel="noreferrer noopener"
                  title={row.domain}
                  noWrap
                  sx={{ fontFamily: "ui-monospace, Menlo, monospace", fontSize: 12.5 }}
                >
                  {row.domain}
                </Link>
                {onSelectLink && (
                  <Tooltip title={t("links.auditHistory")}>
                    <IconButton
                      size="small"
                      onClick={() => onSelectLink(row)}
                      sx={{ p: 0.25, opacity: 0.7, "&:hover": { opacity: 1 } }}
                    >
                      <AssessmentIcon sx={{ fontSize: 15 }} color="primary" />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
              {detail && (
                <Typography variant="caption" color="text.secondary" noWrap>
                  {detail}
                </Typography>
              )}
            </Stack>
          );
        },
      },
      {
        field: "verdict",
        headerName: t("matrix.verdict"),
        // Rộng cố định — nội dung là chip + tỷ lệ, giãn thêm chỉ tạo khoảng trắng.
        width: 150,
        valueGetter: (_v, row) => row.consensus.verdict,
        renderCell: ({ row }) => {
          const { verdict, healthy, total, avgResponseMs } = row.consensus;
          const hint =
            verdict === "partial"
              ? t("verdict.partialHint")
              : verdict === "down"
                ? t("verdict.downHint")
                : "";
          return (
            <Stack sx={{ py: 0.5, lineHeight: 1.3 }}>
              <Tooltip title={hint}>
                <Chip
                  size="small"
                  variant="outlined"
                  color={VERDICT_COLOR[verdict]}
                  label={t(`verdict.${verdict}`)}
                />
              </Tooltip>
              <Typography variant="caption" color="text.secondary">
                {healthy}/{total}
                {avgResponseMs != null && ` · ${formatMs(avgResponseMs)}`}
              </Typography>
            </Stack>
          );
        },
      },
    ];

    const sourceCols: GridColDef<MatrixRow>[] = columnDefs.map(col => ({
      field: col.key,
      headerName: col.deviceId == null ? t("matrix.serverColumn") : col.name,
      // Phần bề rộng dư chia đều cho các cột nguồn — đây mới là nội dung chính.
      flex: 1,
      minWidth: 110,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderHeader: () => {
        // Nguồn server có region cố định là "server" — hiện lại dưới tên cột chỉ tổ thừa.
        const isServer = col.deviceId == null;
        const meta = isServer ? "" : [col.region, col.network].filter(Boolean).join(" · ");
        return (
          <Stack sx={{ lineHeight: 1.25, textAlign: "center", py: 0.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 700 }} noWrap>
              {isServer ? t("matrix.serverColumn") : col.name}
            </Typography>
            {meta && (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }} noWrap>
                {meta}
              </Typography>
            )}
          </Stack>
        );
      },
      renderCell: ({ row }) => <Cell cell={row[col.key] as MatrixCell | undefined} />,
    }));

    return [...base, ...sourceCols];
  }, [columnDefs, t]);

  return (
    <Card
      title={t("matrix.title")}
      actions={
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <TextField
            select
            size="small"
            label={t("matrix.staleLabel")}
            value={String(staleMinutes)}
            onChange={e => setStaleMinutes(Number(e.target.value))}
            sx={{ minWidth: 150 }}
          >
            {[15, 30, 60, 180, 720].map(n => (
              <MenuItem key={n} value={String(n)}>
                {n}
              </MenuItem>
            ))}
          </TextField>
          <Button variant="ghost" onClick={reload}>
            {t("common.refresh")}
          </Button>
        </Stack>
      }
    >
      <p className="muted matrix-sub">{t("matrix.subtitle")}</p>

      {error && <ErrorBox message={error} />}

      {loading ? (
        <Loading />
      ) : !data ? (
        <Empty message={t("matrix.empty")} />
      ) : columnDefs.length === 0 ? (
        <Empty message={t("matrix.noColumns")} />
      ) : (
        <>
          <Stack
            direction="row"
            spacing={2}
            useFlexGap
            sx={{ flexWrap: "wrap", justifyContent: "space-between", alignItems: "center" }}
          >
            <Stack direction="row" spacing={2} useFlexGap sx={{ flexWrap: "wrap" }}>
              {VERDICTS.map(v => (
                <Stack key={v} direction="row" spacing={0.75} sx={{ alignItems: "baseline" }}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 700,
                      color:
                        VERDICT_COLOR[v] === "default" ? "text.secondary" : `${VERDICT_COLOR[v]}.main`,
                    }}
                  >
                    {data.summary[v]}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t(`verdict.${v}`)}
                  </Typography>
                </Stack>
              ))}
            </Stack>

            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={onlyProblems}
                  onChange={e => setOnlyProblems(e.target.checked)}
                />
              }
              label={<Typography variant="body2">{t("matrix.onlyProblems")}</Typography>}
            />
          </Stack>

          {/* Mọi link đều unknown = chưa nguồn nào gửi kết quả, không phải lỗi hiển thị. */}
          {data.summary.total > 0 && data.summary.unknown === data.summary.total && (
            <div className="alert alert-warn">
              {t("matrix.noResultsHint", { n: data.summary.total })}
            </div>
          )}

          {rows.length === 0 ? (
            <Empty message={onlyProblems ? t("matrix.emptyFiltered") : t("matrix.empty")} />
          ) : (
            <DataTable
              rows={rows}
              columns={columns}
              headerHeight={62}
              disableVirtualization
              columnBufferPx={5000}
              initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
              sx={{
                // 1. Cố định hàng 1 tiêu đề (Header row sticky)
                "& .MuiDataGrid-topContainer": {
                  position: "sticky",
                  top: 0,
                  zIndex: 20,
                },
                "& .MuiDataGrid-columnHeaders": {
                  position: "sticky",
                  top: 0,
                  zIndex: 20,
                  backgroundColor: (theme) =>
                    theme.palette.mode === "dark" ? "#0b0e14" : "#eef2f8",
                  borderBottom: "1px solid",
                  borderColor: "divider",
                },
                // 2. Cố định cột Brand (Left 0)
                "& .MuiDataGrid-columnHeader[data-field='brand'], & .MuiDataGrid-cell[data-field='brand']": {
                  position: "sticky !important",
                  left: 0,
                  zIndex: 3,
                  backgroundColor: (theme) =>
                    theme.palette.mode === "dark" ? "#141824" : "#ffffff",
                },
                "& .MuiDataGrid-columnHeader[data-field='brand']": {
                  zIndex: 25,
                  backgroundColor: (theme) =>
                    theme.palette.mode === "dark" ? "#0b0e14" : "#eef2f8",
                },
                // 3. Cố định cột Domain (Left 110px)
                "& .MuiDataGrid-columnHeader[data-field='domain'], & .MuiDataGrid-cell[data-field='domain']": {
                  position: "sticky !important",
                  left: 110,
                  zIndex: 3,
                  backgroundColor: (theme) =>
                    theme.palette.mode === "dark" ? "#141824" : "#ffffff",
                  borderRight: "2px solid",
                  borderColor: "divider",
                  boxShadow: (theme) =>
                    theme.palette.mode === "dark"
                      ? "4px 0 8px -2px rgba(0, 0, 0, 0.5)"
                      : "4px 0 8px -2px rgba(0, 0, 0, 0.12)",
                },
                "& .MuiDataGrid-columnHeader[data-field='domain']": {
                  zIndex: 25,
                  backgroundColor: (theme) =>
                    theme.palette.mode === "dark" ? "#0b0e14" : "#eef2f8",
                },
                // 4. Hover & Selected states cho các ô sticky
                "& .MuiDataGrid-row:hover .MuiDataGrid-cell[data-field='brand'], & .MuiDataGrid-row:hover .MuiDataGrid-cell[data-field='domain']": {
                  backgroundColor: (theme) =>
                    theme.palette.mode === "dark" ? "#1a2030" : "#f7f9fc",
                },
                "& .MuiDataGrid-row.Mui-selected .MuiDataGrid-cell[data-field='brand'], & .MuiDataGrid-row.Mui-selected .MuiDataGrid-cell[data-field='domain']": {
                  backgroundColor: (theme) =>
                    theme.palette.mode === "dark" ? "#182542" : "#e9f0fd",
                },
                "& .MuiDataGrid-row.Mui-selected:hover .MuiDataGrid-cell[data-field='brand'], & .MuiDataGrid-row.Mui-selected:hover .MuiDataGrid-cell[data-field='domain']": {
                  backgroundColor: (theme) =>
                    theme.palette.mode === "dark" ? "#1d2e52" : "#dfeafc",
                },
              }}
            />
          )}

          <Typography variant="caption" color="text.secondary">
            {t("matrix.staleNote", { n: data.staleMinutes })}
          </Typography>
        </>
      )}
    </Card>
  );
}

function Cell({ cell }: { cell: MatrixCell | undefined }) {
  const { t, fmtDate } = useI18n();

  if (!cell || cell.missing || cell.healthStatus == null) {
    return (
      <Tooltip title={t("matrix.missing")}>
        <Typography component="span" color="text.disabled" sx={{ fontSize: 18 }}>
          ·
        </Typography>
      </Tooltip>
    );
  }

  const symbol =
    cell.healthStatus === "healthy" ? "✅" : cell.healthStatus === "unhealthy" ? "❌" : "❔";

  const tooltip = [
    `${t("table.status")}: ${t(`status.${cell.healthStatus}`)}`,
    cell.statusCode != null ? `${t("table.http")}: ${cell.statusCode}` : null,
    cell.responseMs != null ? `${t("table.response")}: ${formatMs(cell.responseMs)}` : null,
    cell.ip ? `${t("table.checkerIp")}: ${cell.ip}` : null,
    (() => {
      const err = describeCheckError(
        t,
        { code: cell.errorCode, statusCode: cell.statusCode, detail: cell.errorDetail },
        cell.error,
      );
      return err ? `${t("table.error")}: ${err}` : null;
    })(),
    `${t("table.checkedAt")}: ${fmtDate(cell.checkedAt)}`,
    cell.stale ? t("matrix.stale") : null,
  ]
    .filter(Boolean)
    .join("\n");

  return (
    <Tooltip title={tooltip}>
      <Typography
        component="span"
        sx={{
          fontSize: 15,
          cursor: "help",
          // Kết quả cũ hơn staleMinutes — làm mờ để không đọc nhầm là dữ liệu tươi.
          ...(cell.stale ? { opacity: 0.32, filter: "grayscale(0.6)" } : {}),
        }}
      >
        {symbol}
      </Typography>
    </Tooltip>
  );
}
